import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { staffAPI, GENDER_OPTIONS } from '../../api/staff';
import { ShieldCheck, Camera, Check, X, AlertTriangle, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

// ══════════════════════════════════════
// HELPER FUNCTIONS
// ══════════════════════════════════════

const toProperCase = (str) =>
  str.trim()
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .replace(/(^|\s)\S/g, l => l.toUpperCase());

const normalizePhone = (str) => {
  const digits = str.replace(/\D/g, '');
  return digits.startsWith('52') ? digits.slice(2) : digits;
};

const typoCorrections = {
  'gmal.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmial.com': 'gmail.com',
  'hotmal.com': 'hotmail.com',
  'hotmial.com': 'hotmail.com',
  'yaho.com': 'yahoo.com',
  'outlok.com': 'outlook.com',
};

const checkEmailTypo = (email) => {
  if (!email) return null;
  const parts = email.split('@');
  if (parts.length !== 2) return null;
  const domain = parts[1].toLowerCase();
  if (typoCorrections[domain]) {
    return `${parts[0]}@${typoCorrections[domain]}`;
  }
  return null;
};

// ══════════════════════════════════════
// STEP BAR COMPONENT
// ══════════════════════════════════════

function StepBar({ current, total }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-6">
      {Array.from({ length: total }, (_, i) => (
        <React.Fragment key={i}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all ${
            i < current ? 'bg-soem-primary border-soem-primary text-white' :
            i === current ? 'border-soem-primary text-soem-primary' : 'border-slate-300 text-slate-400'
          }`}>{i < current ? <Check size={14} /> : i + 1}</div>
          {i < total - 1 && <div className={`flex-1 h-0.5 max-w-8 ${i < current ? 'bg-soem-primary' : 'bg-slate-200'}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

// ══════════════════════════════════════
// TOGGLE COMPONENT
// ══════════════════════════════════════

function Toggle({ value, onChange, label }) {
  // #YesNoBothVisible — antes era un solo botón que flipeaba (mostraba SI o
  // NO según el value). Eso confundía al usuario que pensaba que tenía
  // que elegir activamente. Ahora se ven ambos botones SÍ y NO siempre,
  // con NO marcado por default (value=false). El seleccionado se ve en
  // color, el otro en gris claro.
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0 gap-3">
      <span className="text-sm text-slate-700 flex-1 pr-2">{label}</span>
      <div className="inline-flex rounded-full border border-slate-300 overflow-hidden flex-shrink-0">
        <button type="button" onClick={() => onChange(false)}
          aria-pressed={value === false}
          className={`px-4 py-1.5 text-sm font-bold transition-colors ${value === false ? 'bg-slate-700 text-white' : 'bg-white text-slate-400 hover:bg-slate-50'}`}>
          NO
        </button>
        <button type="button" onClick={() => onChange(true)}
          aria-pressed={value === true}
          className={`px-4 py-1.5 text-sm font-bold transition-colors border-l border-slate-300 ${value === true ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-slate-400 hover:bg-slate-50'}`}>
          SÍ
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════
// FORM INPUT COMPONENT WITH VALIDATION
// ══════════════════════════════════════

function FormInput({ 
  label, 
  name, 
  register, 
  errors, 
  required = false, 
  type = 'text',
  placeholder = '',
  helpText = '',
  inputMode,
  autoComplete,
  maxLength,
  className = '',
  ...props 
}) {
  const error = errors[name];
  const hasError = !!error;
  
  // #M2 — todos los campos del formulario (nombre, teléfono, dirección,
  // cuenta bancaria...) compartían el mismo id="f-field" hardcodeado:
  // clic en cualquier <label> enfocaba el PRIMER input del DOM con ese
  // id, no el campo correspondiente, y el autocompletado del navegador
  // llenaba campos equivocados. Se deriva un id único por campo de `name`.
  const fieldId = `f-${name}`;

  return (
    <div className={className}>
      <label htmlFor={fieldId} className="soem-label block mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <input
          id={fieldId}
          type={type}
          inputMode={inputMode}
          autoComplete={autoComplete}
          maxLength={maxLength}
          placeholder={placeholder}
          className={`w-full px-4 py-3 border rounded-sm text-sm focus:outline-none focus:ring-2 bg-white transition-colors ${
            hasError 
              ? 'border-red-500 focus:ring-red-200' 
              : 'border-slate-300 focus:ring-soem-primary/20 focus:border-soem-primary'
          }`}
          {...register(name)}
          {...props}
        />
      </div>
      {helpText && !hasError && (
        <p className="text-slate-400 text-xs mt-1">{helpText}</p>
      )}
      {hasError && (
        <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
          <AlertTriangle size={12} /> {error.message}
        </p>
      )}
    </div>
  );
}

// ══════════════════════════════════════
// PHOTO UPLOAD COMPONENT
// ══════════════════════════════════════

function PhotoUpload({ photoUrl, setPhotoUrl }) {
  const [photoState, setPhotoState] = useState(photoUrl ? 'success' : 'initial'); // initial, uploading, success, error
  const [photoError, setPhotoError] = useState('');
  const [photoPreview, setPhotoPreview] = useState(photoUrl || null);
  const fileRef = useRef(null);

  const handlePhotoUpload = async (file) => {
    // Validate locally first
    if (file.size > 5 * 1024 * 1024) {
      setPhotoState('error');
      setPhotoError('La foto es muy grande (max. 5MB)');
      return;
    }
    
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setPhotoState('error');
      setPhotoError('Formato no valido. Usa JPG, PNG o WEBP');
      return;
    }

    // Show preview immediately
    setPhotoPreview(URL.createObjectURL(file));
    setPhotoState('uploading');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/public/staff/upload-photo`,
        { method: 'POST', body: formData }
      );
      const data = await res.json();
      
      if (data.success && data.photo_url) {
        setPhotoUrl(data.photo_url);
        setPhotoPreview(data.photo_url);
        setPhotoState('success');
      } else {
        throw new Error(data.error || 'Error desconocido');
      }
    } catch (err) {
      setPhotoState('error');
      setPhotoError('Error al subir la foto. Intenta de nuevo.');
      setPhotoPreview(null);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handlePhotoUpload(file);
    }
    e.target.value = '';
  };

  const handleRetry = () => {
    setPhotoState('initial');
    setPhotoError('');
    setPhotoPreview(null);
    fileRef.current?.click();
  };

  return (
    <div className="flex flex-col items-center">
      {/* Estado 1: INICIAL */}
      {photoState === 'initial' && (
        <div 
          onClick={() => fileRef.current?.click()}
          data-testid="photo-upload-initial"
          className="w-[120px] h-[120px] rounded-full bg-slate-100 border-2 border-dashed border-slate-300 flex flex-col items-center justify-center cursor-pointer hover:border-soem-primary hover:bg-slate-50 transition-all"
        >
          <Camera size={32} className="text-slate-400 mb-1" />
          <span className="text-xs text-slate-500 text-center px-2">Toca para agregar foto</span>
        </div>
      )}

      {/* Estado 2: CARGANDO */}
      {photoState === 'uploading' && (
        <div className="w-[120px] h-[120px] rounded-full bg-slate-100 border-2 border-soem-primary flex flex-col items-center justify-center relative overflow-hidden">
          {photoPreview && (
            <img src={photoPreview} alt="Preview" className="absolute inset-0 w-full h-full object-cover opacity-50" />
          )}
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/30">
            <Loader2 size={32} className="text-white animate-spin" />
          </div>
        </div>
      )}

      {/* Estado 3: EXITO */}
      {photoState === 'success' && (
        <div className="relative" data-testid="photo-upload-success">
          <div className="w-[120px] h-[120px] rounded-full overflow-hidden border-2 border-green-500">
            <img src={photoPreview} alt="Foto de perfil" className="w-full h-full object-cover" />
          </div>
          <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center border-2 border-white">
            <Check size={16} className="text-white" />
          </div>
        </div>
      )}

      {/* Estado 4: ERROR */}
      {photoState === 'error' && (
        <div className="w-[120px] h-[120px] rounded-full bg-red-50 border-2 border-red-400 flex flex-col items-center justify-center">
          <X size={32} className="text-red-500 mb-1" />
          <span className="text-xs text-red-500 text-center px-2">Error</span>
        </div>
      )}

      {/* Textos debajo del circulo */}
      <div className="mt-3 text-center">
        {photoState === 'initial' && (
          <p className="text-xs text-slate-400">JPG, PNG o WEBP - Max. 5MB</p>
        )}
        {photoState === 'uploading' && (
          <>
            <p className="text-sm text-soem-primary font-medium">Subiendo foto...</p>
            <div className="w-32 h-1 bg-slate-200 rounded-full mt-2 overflow-hidden">
              <div className="h-full bg-soem-primary rounded-full animate-pulse" style={{ width: '60%' }} />
            </div>
          </>
        )}
        {photoState === 'success' && (
          <>
            <p className="text-sm text-green-600 font-medium flex items-center gap-1 justify-center">
              <Check size={14} /> Foto guardada correctamente
            </p>
            <button 
              type="button" 
              onClick={() => fileRef.current?.click()}
              className="text-xs text-soem-primary hover:underline mt-1"
            >
              Cambiar foto
            </button>
          </>
        )}
        {photoState === 'error' && (
          <>
            <p className="text-sm text-red-500 font-medium">{photoError}</p>
            <button 
              type="button" 
              onClick={handleRetry}
              className="text-xs text-soem-primary hover:underline mt-1"
            >
              Intentar de nuevo
            </button>
          </>
        )}
      </div>

      <input 
        ref={fileRef}
        type="file" 
        accept="image/jpeg,image/png,image/webp" 
        capture="environment" 
        className="hidden"
        onChange={handleFileChange}
        disabled={photoState === 'uploading'}
      />
    </div>
  );
}

// ══════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════

export default function PublicRegistration() {
  const { eventId } = useParams();
  const [step, setStep] = useState(0);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [pageError, setPageError] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [emailSuggestion, setEmailSuggestion] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Medical toggles state (separate from form)
  const [medicalInfo, setMedicalInfo] = useState({
    has_condition: false,
    condition_detail: '',
    uses_substances: false,
    has_disability: false,
    disability_detail: '',
  });

  const [skipBank, setSkipBank] = useState(false);

  const { 
    register, 
    handleSubmit, 
    formState: { errors, touchedFields }, 
    watch,
    setValue,
    getValues,
    trigger,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      first_name: '',
      last_name: '',
      position: '',
      gender: '',
      age: '',
      phone: '',
      email: '',
      street: '',
      ext_number: '',
      colony: '',
      postal_code: '',
      city: '',
      state: '',
      emergency_name: '',
      emergency_relationship: '',
      emergency_phone: '',
      bank: '',
      account_number: '',
      clabe: '',
      account_holder: '',
      imss_number: '',
    }
  });

  const STEP_LABELS = ['Datos personales', 'Contacto', 'Emergencias y Salud', 'Datos de pago'];
  const TOTAL_STEPS = 5; // Including confirmation step

  useEffect(() => {
    staffAPI.publicEventInfo(eventId)
      .then(r => setEvent(r.data))
      .catch(() => setPageError('Este codigo QR no es valido o el evento no existe'))
      .finally(() => setLoading(false));
  }, [eventId]);

  // Validate current step fields
  const validateStep = async (stepIndex) => {
    let fieldsToValidate = [];
    
    switch (stepIndex) {
      case 0:
        fieldsToValidate = ['first_name', 'last_name', 'gender', 'age'];
        break;
      case 1:
        fieldsToValidate = ['phone', 'email', 'street', 'ext_number', 'colony', 'postal_code', 'city', 'state'];
        break;
      case 2:
        fieldsToValidate = ['emergency_name', 'emergency_relationship', 'emergency_phone'];
        break;
      case 3:
        if (!skipBank) {
          fieldsToValidate = ['bank', 'account_number', 'clabe', 'account_holder'];
        }
        break;
      default:
        break;
    }
    
    const result = await trigger(fieldsToValidate);
    return result;
  };

  const handleNext = async () => {
    const isValid = await validateStep(step);
    
    if (!isValid) {
      // Scroll to first error
      setTimeout(() => {
        const firstError = document.querySelector('.border-red-500');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }
    
    if (step < 3) {
      setStep(s => s + 1);
    } else {
      // Show confirmation
      setShowConfirmation(true);
    }
  };

  const handlePrev = () => {
    if (showConfirmation) {
      setShowConfirmation(false);
    } else if (step > 0) {
      setStep(s => s - 1);
    }
  };

  const onSubmit = async () => {
    setSubmitting(true);
    const data = getValues();
    
    try {
      const payload = {
        first_name: toProperCase(data.first_name),
        last_name: toProperCase(data.last_name),
        position: data.position?.trim() || null,
        gender: data.gender || null,
        age: data.age ? parseInt(data.age) : null,
        phone: normalizePhone(data.phone),
        email: data.email?.toLowerCase().trim() || null,
        photo: photoUrl || null,
        address: {
          street: data.street?.trim() || null,
          ext_number: data.ext_number?.trim() || null,
          neighborhood: data.colony?.trim() || null,
          postal_code: data.postal_code?.trim() || null,
          city: data.city?.trim() || null,
          state: data.state?.trim() || null,
        },
        emergency_contact: {
          name: toProperCase(data.emergency_name),
          relationship: data.emergency_relationship?.trim() || null,
          phone: normalizePhone(data.emergency_phone),
        },
        medical_info: medicalInfo,
        bank_info: !skipBank && data.bank ? {
          bank: toProperCase(data.bank),
          account_number: data.account_number?.trim() || null,
          clabe: data.clabe?.trim() || null,
          account_holder: data.account_holder?.toUpperCase().trim() || null,
        } : null,
        social_security_number: data.imss_number?.trim() || null,
      };

      const r = await staffAPI.publicRegister(eventId, payload);
      setResult(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al registrarse');
    } finally {
      setSubmitting(false);
    }
  };

  // Email typo check on blur
  const handleEmailBlur = (e) => {
    const value = e.target.value?.toLowerCase().trim();
    setValue('email', value);
    const suggestion = checkEmailTypo(value);
    setEmailSuggestion(suggestion);
  };

  const applyEmailSuggestion = () => {
    if (emailSuggestion) {
      setValue('email', emailSuggestion);
      setEmailSuggestion(null);
    }
  };

  // ══════════════════════════════════════
  // LOADING STATE
  // ══════════════════════════════════════
  if (loading) return (
    <div className="min-h-screen bg-soem-bg flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-4 border-soem-primary border-t-transparent" />
    </div>
  );

  // ══════════════════════════════════════
  // ERROR STATE
  // ══════════════════════════════════════
  if (pageError) return (
    <div className="min-h-screen bg-soem-bg flex items-center justify-center p-4">
      <div className="text-center">
        <div className="text-5xl mb-4">
          <X className="w-16 h-16 text-red-500 mx-auto" />
        </div>
        <h2 className="text-xl font-bold text-soem-navy mb-2">{pageError}</h2>
      </div>
    </div>
  );

  // ══════════════════════════════════════
  // SUCCESS STATE
  // ══════════════════════════════════════
  if (result) {
    const firstName = toProperCase(getValues('first_name'));
    const email = getValues('email');
    const displayCode = result.staff_code || result.registration_id || result.id;
    const isPending = !result.staff_code && (result.message?.includes('aprobación') || result.message?.includes('aprobacion') || result.registration_id);
    
    return (
      <div className="min-h-screen bg-soem-bg flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-24 h-24 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center animate-bounce">
            <Check className="w-12 h-12 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-soem-navy mb-2">
            Registro exitoso, {firstName}!
          </h2>
          {isPending ? (
            <>
              <p className="text-slate-500 text-sm mb-4">Tu registro esta pendiente de aprobacion. Tu codigo de seguimiento es:</p>
              <div className="bg-soem-navy text-white text-lg font-bold py-3 px-6 rounded-sm mb-4 font-mono break-all">
                {displayCode}
              </div>
              <p className="text-slate-400 text-xs">El coordinador del evento revisara tu solicitud y te asignara un codigo de personal.</p>
            </>
          ) : (
            <>
              <p className="text-slate-500 text-sm mb-4">Tu codigo de personal es:</p>
              <div className="bg-soem-primary text-white text-3xl font-black py-4 px-8 rounded-sm mb-4 font-mono">
                {displayCode}
              </div>
              <p className="text-slate-400 text-xs">El coordinador del evento ha sido notificado.</p>
            </>
          )}
          {email && (
            <p className="text-slate-400 text-xs mt-1">
              Te enviamos confirmacion a <strong>{email}</strong>
            </p>
          )}
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════
  // CONFIRMATION SCREEN
  // ══════════════════════════════════════
  if (showConfirmation) {
    const data = getValues();
    
    return (
      <div className="min-h-screen bg-soem-bg">
        {/* Header */}
        <div className="bg-soem-navy px-4 py-4 text-white text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <ShieldCheck size={20} className="text-soem-primary" />
            <span className="font-black text-lg">SOEM</span>
          </div>
          {event && (
            <>
              <p className="font-bold">{event.name}</p>
              <p className="text-slate-400 text-xs">
                {event.location_name}
              </p>
            </>
          )}
        </div>

        <div className="max-w-md mx-auto p-4">
          <div className="soem-card p-5 mt-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-3 bg-blue-100 rounded-full flex items-center justify-center">
                <Check className="w-8 h-8 text-soem-primary" />
              </div>
              <h2 className="text-lg font-bold text-soem-navy">Revisa tus datos antes de enviar</h2>
            </div>

            {/* Summary */}
            <div className="space-y-4">
              {/* Photo */}
              {photoUrl && (
                <div className="flex justify-center">
                  <img src={photoUrl} alt="Foto" className="w-20 h-20 rounded-full object-cover border-2 border-slate-200" />
                </div>
              )}

              {/* Personal */}
              <div className="bg-slate-50 rounded-sm p-3">
                <p className="text-xs text-slate-500 mb-1">Nombre completo</p>
                <p className="font-semibold text-soem-navy">{toProperCase(data.first_name)} {toProperCase(data.last_name)}</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-sm p-3">
                  <p className="text-xs text-slate-500 mb-1">Telefono</p>
                  <p className="font-semibold text-soem-navy">{normalizePhone(data.phone)}</p>
                </div>
                {data.email && (
                  <div className="bg-slate-50 rounded-sm p-3">
                    <p className="text-xs text-slate-500 mb-1">Email</p>
                    <p className="font-semibold text-soem-navy text-sm truncate">{data.email}</p>
                  </div>
                )}
              </div>

              {(data.city || data.state) && (
                <div className="bg-slate-50 rounded-sm p-3">
                  <p className="text-xs text-slate-500 mb-1">Ciudad y Estado</p>
                  <p className="font-semibold text-soem-navy">
                    {[data.city, data.state].filter(Boolean).join(', ')}
                  </p>
                </div>
              )}

              <div className="bg-slate-50 rounded-sm p-3">
                <p className="text-xs text-slate-500 mb-1">Contacto de emergencia</p>
                <p className="font-semibold text-soem-navy">{toProperCase(data.emergency_name)}</p>
                <p className="text-sm text-slate-600">{normalizePhone(data.emergency_phone)}</p>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 mt-6">
              <button 
                type="button" 
                onClick={handlePrev} 
                className="soem-btn-secondary flex-1 py-3 text-sm flex items-center justify-center gap-1"
              >
                <ChevronLeft size={16} /> Editar mis datos
              </button>
              <button 
                type="button" 
                onClick={onSubmit} 
                disabled={submitting}
                className="soem-btn-primary flex-1 py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="confirm-register-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Registrando...
                  </>
                ) : (
                  <>
                    <Check size={16} /> Confirmar y enviar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════
  // MAIN FORM
  // ══════════════════════════════════════
  return (
    <div className="min-h-screen bg-soem-bg">
      {/* Header */}
      <div className="bg-soem-navy px-4 py-4 text-white text-center">
        <div className="flex items-center justify-center gap-2 mb-1">
          <ShieldCheck size={20} className="text-soem-primary" />
          <span className="font-black text-lg">SOEM</span>
        </div>
        {event && (
          <>
            <p className="font-bold">{event.name}</p>
            <p className="text-slate-400 text-xs">
              {event.location_name}
              {event.dates?.event_start && ` - ${new Date(event.dates.event_start).toLocaleDateString('es-MX')}`}
            </p>
          </>
        )}
      </div>

      <div className="max-w-md mx-auto p-4">
        <div className="soem-card p-5 mt-4">
          <p className="text-center text-slate-500 text-sm mb-4">
            Completa tus datos para registrarte como personal de seguridad
          </p>
          <StepBar current={step} total={4} />

          {/* Step labels */}
          <p className="text-center text-xs font-semibold text-soem-primary mb-4">
            Paso {step + 1} de 4 - {STEP_LABELS[step]}
          </p>

          <form onSubmit={handleSubmit(onSubmit)}>
            {/* ══════════════════════════════════════ */}
            {/* STEP 0: Personal Data */}
            {/* ══════════════════════════════════════ */}
            {step === 0 && (
              <div className="space-y-4">
                {/* Photo Upload */}
                <div className="mb-6">
                  <label htmlFor="f-foto-de-perfil" className="soem-label block mb-3 text-center">Foto de perfil</label>
                  <PhotoUpload photoUrl={photoUrl} setPhotoUrl={setPhotoUrl} />
                </div>

                {/* Name */}
                <FormInput
                  label="Nombre(s)"
                  name="first_name"
                  required
                  placeholder="Ej: Juan Carlos"
                  autoComplete="given-name"
                  register={register}
                  errors={errors}
                  {...register('first_name', {
                    required: 'El nombre es obligatorio',
                    minLength: { value: 2, message: 'Minimo 2 caracteres' },
                    maxLength: { value: 60, message: 'Maximo 60 caracteres' },
                    pattern: {
                      value: /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s\-']+$/,
                      message: 'Solo letras, acentos, espacios y guiones'
                    },
                    onBlur: (e) => setValue('first_name', toProperCase(e.target.value))
                  })}
                />

                {/* Last Name */}
                <FormInput
                  label="Apellidos"
                  name="last_name"
                  required
                  placeholder="Ej: Garcia Lopez"
                  autoComplete="family-name"
                  register={register}
                  errors={errors}
                  {...register('last_name', {
                    required: 'Los apellidos son obligatorios',
                    minLength: { value: 2, message: 'Minimo 2 caracteres' },
                    maxLength: { value: 60, message: 'Maximo 60 caracteres' },
                    pattern: {
                      value: /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s\-']+$/,
                      message: 'Solo letras, acentos, espacios y guiones'
                    },
                    onBlur: (e) => setValue('last_name', toProperCase(e.target.value))
                  })}
                />

                {/* TAREA 22: Position/Puesto Habitual */}
                <FormInput
                  label="Puesto habitual"
                  name="position"
                  placeholder="Ej: Supervisor de acceso, Guardia, Paramedico"
                  autoComplete="organization-title"
                  register={register}
                  errors={errors}
                  {...register('position', {
                    maxLength: { value: 80, message: 'Maximo 80 caracteres' }
                  })}
                />

                {/* Gender */}
                <div>
                  <label htmlFor="f-gender" className="soem-label block mb-1">
                    Genero <span className="text-red-500">*</span>
                  </label>
                  <select id="f-gender"
                    className={`w-full px-4 py-3 border rounded-sm text-sm focus:outline-none focus:ring-2 bg-white ${
                      errors.gender ? 'border-red-500' : 'border-slate-300 focus:ring-soem-primary/20 focus:border-soem-primary'
                    }`}
                    {...register('gender', { required: 'Selecciona tu genero' })}
                  >
                    <option value="">-- Seleccionar --</option>
                    <option value="masculino">Masculino</option>
                    <option value="femenino">Femenino</option>
                  </select>
                  {errors.gender && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertTriangle size={12} /> {errors.gender.message}
                    </p>
                  )}
                </div>

                {/* Age */}
                <FormInput
                  label="Edad"
                  name="age"
                  type="number"
                  inputMode="numeric"
                  placeholder="Ej: 28"
                  register={register}
                  errors={errors}
                  {...register('age', {
                    min: { value: 16, message: 'Edad minima 16 años' },
                    max: { value: 99, message: 'Edad maxima 99 años' },
                    validate: v => !v || Number.isInteger(Number(v)) || 'Ingresa un numero entero'
                  })}
                  onKeyDown={e => ['.', ',', 'e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
                />
              </div>
            )}

            {/* ══════════════════════════════════════ */}
            {/* STEP 1: Contact */}
            {/* ══════════════════════════════════════ */}
            {step === 1 && (
              <div className="space-y-4">
                {/* Phone */}
                <div>
                  <FormInput
                    label="Telefono"
                    name="phone"
                    required
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    placeholder="55 1234 5678"
                    helpText="10 dígitos. Formato: 55 1234 5678"
                    maxLength={14}
                    register={register}
                    errors={errors}
                    {...register('phone', {
                      required: 'El telefono es obligatorio',
                      validate: v => {
                        const clean = normalizePhone(v);
                        return clean.length === 10 || 'Ingresa 10 dígitos válidos (ej: 55 1234 5678)';
                      },
                      // #PhoneRealtimeStrip — antes el usuario podía escribir
                      // letras y solo se normalizaba al perder el foco. Ahora
                      // a cada keystroke removemos cualquier no-dígito y
                      // limitamos a 14 chars (10 dígitos + 4 espacios).
                      onChange: (e) => {
                        const cleaned = e.target.value.replace(/[^\d\s]/g, '').slice(0, 14);
                        if (cleaned !== e.target.value) {
                          setValue('phone', cleaned, { shouldValidate: false });
                        }
                      },
                      onBlur: (e) => setValue('phone', normalizePhone(e.target.value))
                    })}
                  />
                </div>

                {/* Email */}
                <div>
                  <FormInput
                    label="Correo electronico"
                    name="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="nombre@correo.com"
                    register={register}
                    errors={errors}
                    {...register('email', {
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: 'Formato invalido. Ej: nombre@correo.com'
                      },
                      onBlur: handleEmailBlur
                    })}
                  />
                  {emailSuggestion && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-2 mt-1 flex items-center justify-between">
                      <span className="text-xs text-yellow-800">Quisiste decir {emailSuggestion}?</span>
                      <button 
                        type="button" 
                        onClick={applyEmailSuggestion}
                        className="text-xs text-soem-primary font-semibold hover:underline"
                      >
                        Si, corregir
                      </button>
                    </div>
                  )}
                </div>

                <div className="border-t border-slate-200 pt-4 mt-4">
                  <p className="soem-label mb-3">Direccion</p>
                  
                  <div className="space-y-3">
                    <FormInput
                      label="Calle"
                      name="street"
                      autoComplete="street-address"
                      placeholder="Ej: Av. Insurgentes Sur"
                      maxLength={100}
                      register={register}
                      errors={errors}
                      {...register('street', {
                        maxLength: { value: 100, message: 'Maximo 100 caracteres' }
                      })}
                    />

                    <FormInput
                      label="Numero Exterior"
                      name="ext_number"
                      placeholder="Ej: 123 o S/N"
                      maxLength={10}
                      register={register}
                      errors={errors}
                      {...register('ext_number', {
                        maxLength: { value: 10, message: 'Maximo 10 caracteres' }
                      })}
                    />

                    <FormInput
                      label="Colonia"
                      name="colony"
                      placeholder="Ej: Del Valle"
                      maxLength={80}
                      register={register}
                      errors={errors}
                      {...register('colony', {
                        maxLength: { value: 80, message: 'Maximo 80 caracteres' },
                        onBlur: (e) => setValue('colony', toProperCase(e.target.value))
                      })}
                    />

                    <FormInput
                      label="Codigo Postal"
                      name="postal_code"
                      inputMode="numeric"
                      autoComplete="postal-code"
                      placeholder="Ej: 06600"
                      maxLength={5}
                      register={register}
                      errors={errors}
                      {...register('postal_code', {
                        pattern: {
                          value: /^\d{5}$/,
                          message: 'El CP debe tener exactamente 5 dígitos'
                        },
                        // #DigitsOnlyStrip — solo dígitos en tiempo real.
                        onChange: (e) => {
                          const cleaned = e.target.value.replace(/\D/g, '').slice(0, 5);
                          if (cleaned !== e.target.value) {
                            setValue('postal_code', cleaned, { shouldValidate: false });
                          }
                        },
                      })}
                    />

                    <FormInput
                      label="Ciudad"
                      name="city"
                      autoComplete="address-level2"
                      placeholder="Ej: Ciudad de Mexico"
                      maxLength={60}
                      register={register}
                      errors={errors}
                      {...register('city', {
                        maxLength: { value: 60, message: 'Maximo 60 caracteres' },
                        onBlur: (e) => setValue('city', toProperCase(e.target.value))
                      })}
                    />

                    <FormInput
                      label="Estado"
                      name="state"
                      autoComplete="address-level1"
                      placeholder="Ej: Ciudad de Mexico (CDMX)"
                      maxLength={60}
                      register={register}
                      errors={errors}
                      {...register('state', {
                        maxLength: { value: 60, message: 'Maximo 60 caracteres' },
                        onBlur: (e) => setValue('state', toProperCase(e.target.value))
                      })}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* ══════════════════════════════════════ */}
            {/* STEP 2: Emergency + Medical */}
            {/* ══════════════════════════════════════ */}
            {step === 2 && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-xs text-blue-800 flex items-start gap-2">
                  <ShieldCheck size={16} className="flex-shrink-0 mt-0.5" />
                  <span>Esta informacion es estrictamente confidencial y solo sera vista por el coordinador del evento.</span>
                </div>

                <div className="space-y-3">
                  <p className="soem-label">Contacto de emergencia</p>
                  
                  <FormInput
                    label="Nombre completo"
                    name="emergency_name"
                    required
                    placeholder="Ej: Maria Garcia"
                    register={register}
                    errors={errors}
                    {...register('emergency_name', {
                      required: 'El nombre del contacto es obligatorio',
                      minLength: { value: 2, message: 'Minimo 2 caracteres' },
                      maxLength: { value: 80, message: 'Maximo 80 caracteres' },
                      pattern: {
                        value: /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s\-']+$/,
                        message: 'Solo letras y acentos'
                      },
                      onBlur: (e) => setValue('emergency_name', toProperCase(e.target.value))
                    })}
                  />

                  <FormInput
                    label="Parentesco"
                    name="emergency_relationship"
                    required
                    placeholder="Ej: Madre, Esposo/a, Hermano/a"
                    maxLength={30}
                    register={register}
                    errors={errors}
                    {...register('emergency_relationship', {
                      required: 'El parentesco es obligatorio',
                      maxLength: { value: 30, message: 'Maximo 30 caracteres' }
                    })}
                  />

                  <FormInput
                    label="Telefono"
                    name="emergency_phone"
                    required
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    placeholder="55 1234 5678"
                    helpText="10 dígitos"
                    maxLength={14}
                    register={register}
                    errors={errors}
                    {...register('emergency_phone', {
                      required: 'El telefono es obligatorio',
                      validate: v => {
                        const clean = normalizePhone(v);
                        return clean.length === 10 || 'Ingresa 10 dígitos válidos';
                      },
                      // #PhoneRealtimeStrip — mismo patrón que phone.
                      onChange: (e) => {
                        const cleaned = e.target.value.replace(/[^\d\s]/g, '').slice(0, 14);
                        if (cleaned !== e.target.value) {
                          setValue('emergency_phone', cleaned, { shouldValidate: false });
                        }
                      },
                      onBlur: (e) => setValue('emergency_phone', normalizePhone(e.target.value))
                    })}
                  />
                </div>

                <div className="border-t border-slate-200 pt-4">
                  <p className="soem-label mb-2">Preguntas de salud</p>
                  <div className="border border-slate-200 rounded-sm divide-y divide-slate-100">
                    {/* Has condition */}
                    <div className="px-3">
                      <Toggle 
                        value={medicalInfo.has_condition}
                        onChange={v => setMedicalInfo(m => ({...m, has_condition: v}))}
                        label="Tiene alguna enfermedad o condicion medica?" 
                      />
                      {medicalInfo.has_condition && (
                        <div className="mb-3">
                          <textarea 
                            className="w-full px-4 py-3 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary/20 resize-none"
                            rows={2}
                            placeholder="Describe brevemente..."
                            maxLength={500}
                            value={medicalInfo.condition_detail}
                            onChange={e => setMedicalInfo(m => ({...m, condition_detail: e.target.value}))}
                          />
                          <p className="text-xs text-slate-400 text-right">{medicalInfo.condition_detail.length}/500</p>
                        </div>
                      )}
                    </div>

                    {/* Uses substances */}
                    <div className="px-3">
                      <Toggle 
                        value={medicalInfo.uses_substances}
                        onChange={v => setMedicalInfo(m => ({...m, uses_substances: v}))}
                        label="Consume algun tipo de drogas o sustancias?" 
                      />
                    </div>

                    {/* Has disability */}
                    <div className="px-3">
                      <Toggle 
                        value={medicalInfo.has_disability}
                        onChange={v => setMedicalInfo(m => ({...m, has_disability: v}))}
                        label="Tiene alguna discapacidad?" 
                      />
                      {medicalInfo.has_disability && (
                        <div className="mb-3">
                          <textarea 
                            className="w-full px-4 py-3 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary/20 resize-none"
                            rows={2}
                            placeholder="Describe brevemente..."
                            maxLength={500}
                            value={medicalInfo.disability_detail}
                            onChange={e => setMedicalInfo(m => ({...m, disability_detail: e.target.value}))}
                          />
                          <p className="text-xs text-slate-400 text-right">{medicalInfo.disability_detail.length}/500</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* IMSS */}
                <div className="border-t border-slate-200 pt-4">
                  <FormInput
                    label="NSS IMSS"
                    name="imss_number"
                    inputMode="numeric"
                    placeholder="12345678901"
                    helpText="11 dígitos del Número de Seguridad Social"
                    maxLength={11}
                    register={register}
                    errors={errors}
                    {...register('imss_number', {
                      pattern: {
                        value: /^\d{11}$/,
                        message: 'El NSS debe tener 11 dígitos'
                      },
                      // #DigitsOnlyStrip — strip non-digits en tiempo real.
                      onChange: (e) => {
                        const cleaned = e.target.value.replace(/\D/g, '').slice(0, 11);
                        if (cleaned !== e.target.value) {
                          setValue('imss_number', cleaned, { shouldValidate: false });
                        }
                      },
                    })}
                  />
                </div>
              </div>
            )}

            {/* ══════════════════════════════════════ */}
            {/* STEP 3: Bank Info */}
            {/* ══════════════════════════════════════ */}
            {step === 3 && (
              <div className="space-y-4">
                <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs text-slate-600 flex items-start gap-2">
                  <ShieldCheck size={16} className="flex-shrink-0 mt-0.5" />
                  <span>Datos para procesar tu pago. Protegidos y encriptados.</span>
                </div>

                {!skipBank ? (
                  <>
                    <FormInput
                      label="Banco"
                      name="bank"
                      placeholder="Ej: BBVA, Santander, Banorte"
                      maxLength={50}
                      register={register}
                      errors={errors}
                      {...register('bank', {
                        maxLength: { value: 50, message: 'Maximo 50 caracteres' },
                        onBlur: (e) => setValue('bank', toProperCase(e.target.value))
                      })}
                    />

                    <FormInput
                      label="Numero de cuenta"
                      name="account_number"
                      inputMode="numeric"
                      placeholder="10 a 18 digitos"
                      register={register}
                      errors={errors}
                      {...register('account_number', {
                        pattern: {
                          value: /^\d{10,18}$/,
                          message: 'Entre 10 y 18 digitos numericos'
                        }
                      })}
                    />

                    <FormInput
                      label="CLABE interbancaria"
                      name="clabe"
                      inputMode="numeric"
                      placeholder="18 digitos"
                      helpText="La encuentras en tu app bancaria"
                      register={register}
                      errors={errors}
                      {...register('clabe', {
                        pattern: {
                          value: /^\d{18}$/,
                          message: 'La CLABE debe tener exactamente 18 digitos'
                        }
                      })}
                    />

                    <FormInput
                      label="Titular de la cuenta"
                      name="account_holder"
                      placeholder="COMO APARECE EN TU CUENTA"
                      maxLength={80}
                      register={register}
                      errors={errors}
                      {...register('account_holder', {
                        maxLength: { value: 80, message: 'Maximo 80 caracteres' },
                        pattern: {
                          value: /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s\-']+$/,
                          message: 'Solo letras y acentos'
                        },
                        onBlur: (e) => setValue('account_holder', e.target.value.toUpperCase())
                      })}
                    />

                    <button 
                      type="button" 
                      onClick={() => setSkipBank(true)}
                      className="w-full py-3 text-sm text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      Prefiero agregarlo despues
                    </button>
                  </>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-slate-400 text-sm mb-3">Omitiras datos bancarios por ahora</p>
                    <button 
                      type="button" 
                      onClick={() => setSkipBank(false)}
                      className="text-soem-primary text-sm font-semibold hover:underline"
                    >
                      Agregar datos bancarios
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ══════════════════════════════════════ */}
            {/* Navigation */}
            {/* ══════════════════════════════════════ */}
            <div className="flex gap-3 mt-6">
              {step > 0 && (
                <button 
                  type="button" 
                  onClick={handlePrev} 
                  className="soem-btn-secondary flex-1 py-3 text-sm flex items-center justify-center gap-1"
                >
                  <ChevronLeft size={16} /> Anterior
                </button>
              )}
              <button 
                type="button" 
                onClick={handleNext}
                className="soem-btn-primary flex-1 py-3 text-sm flex items-center justify-center gap-1"
              >
                {step < 3 ? (
                  <>Siguiente <ChevronRight size={16} /></>
                ) : (
                  <>Revisar y enviar <Check size={16} /></>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
